// 공개 시공사례 페이지·소개 링크에서 온 상담 문의의 from·page·case·ref 값 — 형식 검증과 유입 경로 결정. 저장소 밖에 두어 인메모리·Supabase 가 한 벌을 쓴다.

import { siteParamSchema, type IntakeSiteDecision } from "./intake-site";
import type { IntakeSource } from "./types";
import { isPublicPageCode } from "../public-page/rules";
import { isReferralCode } from "../referral/rules";
import type { ReferralLinkRef } from "../referral/types";

// 폼 hidden 칸·주소창 이름 — 공개 페이지의 「상담 문의」 가 붙여 보낸다.
export const FROM_FIELD = "from";
export const PAGE_FIELD = "page";
export const CASE_FIELD = "case";
export const REF_FIELD = "ref";

// `from` 에 이 값이 있으면 공개 페이지(기본 목록 포함)에서 누른 문의다.
export const FROM_PORTFOLIO = "portfolio";
// `from` 이 이 값이고 ref 가 소개 링크 주소면 `/s/r/{code}` 로 들어온 문의다.
export const FROM_REFERRAL = "referral";

// `/s/p/{code}` 가 목록으로 넘길 때 붙이는 이름 — 목록·상세가 들고 다니다 문의 버튼에 옮긴다.
export const VIA_FIELD = "via";

type Ref = { id: string; org_id: string };

export interface IntakeOrigin {
  from: boolean;
  page: string | null;
  case: string | null;
  referral: string | null;
}

export const NO_ORIGIN: IntakeOrigin = { from: false, page: null, case: null, referral: null };

const first = (raw: unknown): unknown => (Array.isArray(raw) ? raw[0] : raw);

// 링크 주소 — 형식이 틀리면 조용히 없는 것으로 본다(고객에게 오류를 보이지 않는다).
export const parsePublicPageCode = (raw: unknown): string | null => {
  const value = first(raw);
  return typeof value === "string" && isPublicPageCode(value.trim()) ? value.trim() : null;
};

// 사례 id 는 현장 id 와 같은 모양(uuid · 인메모리 접두 id)이라 같은 스키마를 쓴다.
export const parseCaseParam = (raw: unknown): string | null => {
  const value = first(raw);
  if (typeof value !== "string") return null;
  const parsed = siteParamSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseFromParam = (raw: unknown): boolean => first(raw) === FROM_PORTFOLIO;

// 소개 링크 주소 — from=referral 일 때만 · 형식이 틀리면 조용히 없는 것으로 본다.
export const parseReferralParam = (from: unknown, raw: unknown): string | null => {
  if (first(from) !== FROM_REFERRAL) return null;
  const value = first(raw);
  return typeof value === "string" && isReferralCode(value.trim()) ? value.trim() : null;
};

// 주소창·폼 어느 쪽이든 이름으로 꺼내는 함수 하나만 받는다.
export const parseIntakeOrigin = (get: (key: string) => unknown): IntakeOrigin => ({
  from: parseFromParam(get(FROM_FIELD)),
  page: parsePublicPageCode(get(PAGE_FIELD)),
  case: parseCaseParam(get(CASE_FIELD)),
  referral: parseReferralParam(get(FROM_FIELD), get(REF_FIELD)),
});

// 공개 페이지 → 간편접수 주소 — 링크·사례가 없으면 그 칸을 빼고 `from` 만 붙인다.
export const intakeFromPublicPath = (slug: string, via: string | null, caseId: string | null): string => {
  const query = new URLSearchParams({ [FROM_FIELD]: FROM_PORTFOLIO });
  if (via) query.set(PAGE_FIELD, via);
  if (caseId) query.set(CASE_FIELD, caseId);
  return `/s/intake/${encodeURIComponent(slug)}?${query.toString()}`;
};

// 소개 링크 → 간편접수 주소.
export const intakeFromReferralPath = (slug: string, code: string): string =>
  `/s/intake/${encodeURIComponent(slug)}?${new URLSearchParams({ [FROM_FIELD]: FROM_REFERRAL, [REF_FIELD]: code }).toString()}`;

// 공개 목록·상세 주소에 링크 주소를 이어 붙인다 — 없으면 그대로.
export const withVia = (path: string, via: string | null): string =>
  via ? `${path}${path.includes("?") ? "&" : "?"}${VIA_FIELD}=${encodeURIComponent(via)}` : path;

export interface IntakeSourceDecision {
  source: Extract<IntakeSource, "intake" | "notice" | "page" | "referral">;
  source_project_id: string | null;
  source_public_page_id: string | null;
  source_portfolio_id: string | null;
  referred_by_customer_id: string | null;
}

// uuid 는 대소문자를 가리지 않는다 — DB 가 돌려준 소문자 id 와 주소창 값을 소문자로 맞춰 본다.
const inOrg = (orgId: string, ref: Ref | null, id?: string | null): string | null =>
  ref && ref.org_id === orgId && (id === undefined || ref.id.toLowerCase() === id?.toLowerCase()) ? ref.id : null;

// 안내문 현장 → 소개(켜진 같은 조직 링크만) → 공개 페이지(표시·같은 조직 링크·사례 중 하나) → 기존 간편접수. 다른 조직 링크·사례는 비운다.
export const decideIntakeSource = (
  orgId: string,
  site: IntakeSiteDecision,
  origin: IntakeOrigin,
  refs: { page: Ref | null; portfolio: Ref | null; referral: ReferralLinkRef | null }
): IntakeSourceDecision => {
  const none = { source_public_page_id: null, source_portfolio_id: null, referred_by_customer_id: null };
  if (site.source === "notice") return { source: "notice", source_project_id: site.source_project_id, ...none };
  const referral = origin.referral ? refs.referral : null;
  if (referral && referral.active && referral.org_id === orgId) {
    return { source: "referral", source_project_id: null, ...none, referred_by_customer_id: referral.customer_id };
  }
  const pageId = origin.page ? inOrg(orgId, refs.page) : null;
  const portfolioId = origin.case ? inOrg(orgId, refs.portfolio, origin.case) : null;
  if (origin.from || pageId || portfolioId) {
    return { source: "page", source_project_id: null, source_public_page_id: pageId, source_portfolio_id: portfolioId, referred_by_customer_id: null };
  }
  return { source: "intake", source_project_id: null, ...none };
};
